import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles, QrCode, Crown, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useGuruMessages, useGuruQuota, sendGuruMessage, type GuruMessage } from "@/hooks/useGuruChat";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { ActionCard, PalpiteCard } from "./ToolActionCard";
import { trackEvent } from "@/utils/analyticsTrack";
import GuruModeSelector, { type GuruMode } from "./GuruModeSelector";

interface ChatWindowProps {
  threadId: string;
  onThreadUpdated?: () => void;
}

const QUICK_PROMPTS = [
  "Sonhei com cobra, qual o palpite?",
  "Qual o melhor palpite para hoje?",
  "Sugira um método para usar agora",
];

const QUICK_ACTIONS: { emoji: string; label: string; prompt: string; key: string }[] = [
  { emoji: "🔮", label: "Interpretar sonho", prompt: "Quero interpretar um sonho. Pergunte-me o que sonhei e me ajude a encontrar o grupo e dezenas associadas.", key: "sonho" },
  { emoji: "🧠", label: "Palpite do dia", prompt: "Gere um palpite do dia com grupos e dezenas usando análise estatística.", key: "palpite" },
  { emoji: "📊", label: "Ver tendência", prompt: "Qual é a tendência forte do dia hoje no jogo do bicho?", key: "tendencia" },
  { emoji: "🐾", label: "Buscar bicho", prompt: "Quais bichos estão saindo com mais frequência agora?", key: "buscar" },
  { emoji: "🎯", label: "Melhor ferramenta agora", prompt: "Qual ferramenta do app eu deveria usar agora?", key: "ferramenta" },
];

const MODE_STORAGE_KEY = "guru_chat_mode";

const ChatWindow: React.FC<ChatWindowProps> = ({ threadId, onThreadUpdated }) => {
  const { user } = useAuth();
  const { messages, appendLocal } = useGuruMessages(threadId);
  const quota = useGuruQuota();
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState<GuruMode>(() => {
    try {
      const stored = localStorage.getItem(MODE_STORAGE_KEY);
      if (stored === "estrategico" || stored === "mistico" || stored === "estatistico") return stored;
    } catch {}
    return "estrategico";
  });
  const { toast } = useToast();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, sending]);

  useEffect(() => { inputRef.current?.focus(); }, [threadId]);

  useEffect(() => {
    trackEvent("guru_chat_opened" as any, { threadId });
  }, [threadId]);

  const handleModeChange = (m: GuruMode) => {
    setMode(m);
    try { localStorage.setItem(MODE_STORAGE_KEY, m); } catch {}
    trackEvent("guru_chat_mode_changed" as any, { mode: m });
  };

  const handleQuickAction = (key: string, prompt: string) => {
    trackEvent("guru_chat_quick_action" as any, { action: key });
    void handleSend(prompt);
  };

  const handleSend = async (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || sending || !user) return;
    if (!quota.canSend) {
      trackEvent("guru_chat_quota_blocked" as any, { threadId });
      toast({ title: "Limite diário atingido", description: "Escolha um plano abaixo para continuar.", variant: "destructive" });
      return;
    }
    setSending(true);
    setInput("");
    const optimistic: GuruMessage = {
      id: `local-${Date.now()}`,
      role: "user",
      content: msg,
      parts: null,
      created_at: new Date().toISOString(),
    };
    appendLocal(optimistic);
    try {
      const res = await sendGuruMessage(threadId, msg, mode);
      if (res?.message) appendLocal(res.message as any);
      trackEvent("guru_chat_message_sent" as any, { threadId, mode });
      quota.reload();
      onThreadUpdated?.();
    } catch (e: any) {
      const status = e?.status;
      const reason = e?.payload?.reason;
      const isPremium = quota.isPremium;
      if (status === 402 && reason === "ai_gateway") {
        toast({
          title: "Sistema de IA temporariamente indisponível",
          description: "Estamos com instabilidade no serviço de IA. Tente novamente em instantes. Se persistir, fale com o suporte no WhatsApp (73) 98198-8263.",
          variant: "destructive",
        });
      } else if (status === 402 && !isPremium) {
        toast({ title: "Limite diário atingido", description: "Escolha um plano abaixo para continuar.", variant: "destructive" });
        quota.reload();
      } else if (status === 402 && isPremium) {
        // Assinante recebeu 402 sem reason claro — tratar como indisponibilidade, NÃO como cota
        toast({
          title: "Sistema de IA temporariamente indisponível",
          description: "Seu plano está ativo. Tente novamente em instantes ou fale com o suporte no WhatsApp (73) 98198-8263.",
          variant: "destructive",
        });
      } else if (status === 429) {
        toast({ title: "Muitas requisições", description: "Aguarde um instante e tente novamente.", variant: "destructive" });
      } else {
        toast({ title: "Erro no Guru Chat", description: e?.payload?.message || "Tente novamente.", variant: "destructive" });
      }
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const renderMessage = (m: GuruMessage) => {
    const isUser = m.role === "user";
    return (
      <div key={m.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
        <div className={`max-w-[85%] ${isUser ? "" : "w-full"}`}>
          {isUser ? (
            <div className="rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-primary-foreground whitespace-pre-wrap break-words">
              {m.content}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 p-1"><Sparkles className="h-3 w-3 text-zinc-900" /></div>
                <span className="text-xs font-semibold bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent">O Guru</span>
              </div>
              <div className="text-foreground whitespace-pre-wrap break-words leading-relaxed">{m.content}</div>
              {Array.isArray(m.parts) && m.parts.map((p, i) => {
                if (p.type === "action") return <ActionCard key={i} data={p.data} />;
                if (p.type === "palpite") return <PalpiteCard key={i} data={p.data} />;
                return null;
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 overflow-hidden">
      {/* Top bar: mode selector + quota */}
      <div className="px-3 py-2 border-b border-border bg-card/30 flex items-center justify-between gap-2 flex-wrap">
        <GuruModeSelector mode={mode} onChange={handleModeChange} />
        {!quota.isPremium && (
          <div className="text-[11px] text-muted-foreground flex items-center gap-2">
            <span>Free: {quota.used}/{quota.dailyLimit} hoje</span>
            {!quota.canSend && (
              <Button size="sm" variant="link" className="h-auto p-0 text-amber-500" onClick={() => navigate("/planos#planos")}>
                Upgrade
              </Button>
            )}
          </div>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-none p-4 space-y-4 min-h-0">
        {messages.length === 0 && !sending && (
          <div className="text-center py-8 space-y-4">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-600 shadow-lg shadow-amber-500/30">
              <Sparkles className="h-7 w-7 text-zinc-900" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Eu sou <span className="bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent">O Guru</span></h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mt-1">
                Conte um sonho, peça uma análise do dia ou me diga sua intuição. Eu leio os sinais e te aponto a melhor ferramenta.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 pt-2">
              {QUICK_PROMPTS.map((p) => (
                <Button key={p} size="sm" variant="outline" onClick={() => handleSend(p)} disabled={!quota.canSend}>
                  {p}
                </Button>
              ))}
            </div>
          </div>
        )}
        {messages.map(renderMessage)}
        {sending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-amber-500" /> O Guru está consultando os sinais…
          </div>
        )}
      </div>

      <div className="border-t border-border p-3 bg-card/30 space-y-2">
        {!quota.isPremium && !quota.canSend && (
          <div className="rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-yellow-500/5 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <Lock className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-foreground">
                Você atingiu o limite gratuito de hoje. <strong>Desbloqueie conversas ilimitadas</strong> com O Guru — escolha como continuar:
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                size="sm"
                onClick={() => navigate("/planos#planos")}
                className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-zinc-900 font-bold hover:from-amber-600 hover:to-yellow-600"
              >
                <Crown className="h-4 w-4 mr-1" /> Ver planos Premium
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/pagamento#pix-planos")}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold hover:from-emerald-600 hover:to-green-700"
              >
                <QrCode className="h-4 w-4 mr-1" /> Pagar com PIX
              </Button>
            </div>
          </div>
        )}
        {/* Atalhos rápidos acima do input */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-none -mx-1 px-1 pb-1">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.key}
              onClick={() => handleQuickAction(a.key, a.prompt)}
              disabled={!quota.canSend || sending}
              className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 text-amber-700 dark:text-amber-300 hover:from-amber-500/20 hover:to-yellow-500/20 transition-colors disabled:opacity-50"
            >
              <span>{a.emoji}</span>
              <span>{a.label}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-end">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void handleSend(); }
            }}
            placeholder={quota.canSend ? "Consulte o Guru…" : "Limite diário atingido — faça upgrade"}
            disabled={!quota.canSend || sending}
            className="min-h-[44px] max-h-32 resize-none"
            rows={1}
          />
          <Button
            onClick={() => void handleSend()}
            disabled={!input.trim() || sending || !quota.canSend}
            size="icon"
            className="h-11 w-11 bg-gradient-to-r from-amber-600 to-yellow-500 text-zinc-900 hover:from-amber-700 hover:to-yellow-600 shrink-0"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center">
          📊 Plataforma baseada em análises estatísticas. Sem garantia de premiação.
        </p>
      </div>
    </div>
  );
};

export default ChatWindow;
